import {
  BarChart3,
  Bell,
  Briefcase,
  Building2,
  Clock,
  CreditCard,
  Eye,
  FileText,
  GitBranch,
  LogOut,
  Palette,
  PhoneCall,
  Sparkles,
  User,
  UserCheck,
  Users,
} from 'lucide-react-native';
import { useEffect, useRef, useState } from 'react';
import { Platform, Pressable, ScrollView, StyleSheet, useWindowDimensions } from 'react-native';
import { Box } from '../../../components/primitives/Box';
import { ScrollEdgeFade, useScrollEdges } from '../../../components/primitives/ScrollEdgeFade';
import { VStack } from '../../../components/primitives/Stack';
import { Text } from '../../../components/primitives/Text';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import type { SettingsSection } from '../types';

interface SettingsNavProps {
  activeSection: SettingsSection;
  onSectionChange: (section: SettingsSection) => void;
}

const NAV_ITEMS: { key: SettingsSection; labelKey: string; icon: typeof User }[] = [
  { key: 'profile', labelKey: 'settings.nav.profile', icon: User },
  { key: 'organization', labelKey: 'settings.nav.organization', icon: Building2 },
  { key: 'businessContext', labelKey: 'settings.nav.businessContext', icon: Sparkles },
  { key: 'team', labelKey: 'settings.nav.team', icon: Users },
  { key: 'sellerProfiles', labelKey: 'settings.nav.sellerProfiles', icon: UserCheck },
  { key: 'leadAssignment', labelKey: 'settings.nav.leadAssignment', icon: GitBranch },
  { key: 'jobTitles', labelKey: 'settings.nav.jobTitles', icon: Briefcase },
  { key: 'notifications', labelKey: 'settings.nav.notifications', icon: Bell },
  { key: 'appearance', labelKey: 'settings.nav.appearance', icon: Palette },
  { key: 'analytics', labelKey: 'settings.nav.analytics', icon: BarChart3 },
  { key: 'boardVisibility', labelKey: 'settings.nav.boardVisibility', icon: Eye },
  { key: 'businessHours', labelKey: 'settings.nav.businessHours', icon: Clock },
  { key: 'voiceCalling', labelKey: 'settings.nav.voiceCalling', icon: PhoneCall },
  { key: 'regulatoryDocs', labelKey: 'settings.nav.regulatoryDocs', icon: FileText },
  { key: 'billing', labelKey: 'settings.nav.billing', icon: CreditCard },
  { key: 'account', labelKey: 'settings.nav.account', icon: LogOut },
];

const ICON_SIZE = 20;

function DesktopNav({ activeSection, onSectionChange }: Readonly<SettingsNavProps>) {
  const { t } = useTranslation();
  // Fifteen sections do not fit a short window. The content column beside this
  // one has always scrolled; this column did not, so anything past "Regulatory
  // Docs" was clipped by an ancestor and simply unreachable — no scrollbar, no
  // wheel target, nothing. It now shrinks and scrolls, with fades marking it.
  const { atStart, atEnd, scrollProps } = useScrollEdges();

  return (
    <Box
      backgroundColor={colors.background.card}
      borderRadius="xl"
      borderWidth={1}
      borderColor={colors.border.muted}
      padding="sm"
      style={styles.desktopCard}
    >
      <ScrollView showsVerticalScrollIndicator={false} {...scrollProps}>
        <VStack spacing="xs">
          {NAV_ITEMS.map((item) => {
            const active = item.key === activeSection;
            const danger = item.key === 'account';
            const IconComponent = item.icon;
            const iconColor = active
              ? danger
                ? colors.status.error.light
                : colors.primary[400]
              : colors.text.secondary;
            const textColor = iconColor;

            return (
              <Pressable
                key={item.key}
                testID={`settings_nav_${item.key}_tab`}
                onPress={() => onSectionChange(item.key)}
                style={({ pressed }) => [
                  styles.desktopItem,
                  active && (danger ? styles.desktopItemDanger : styles.desktopItemActive),
                  pressed && !active && styles.desktopItemPressed,
                ]}
                accessibilityRole="tab"
                accessibilityState={{ selected: active }}
              >
                <IconComponent size={ICON_SIZE} color={iconColor} />
                <Text variant="body" weight="medium" color={textColor} style={styles.desktopLabel}>
                  {t(item.labelKey)}
                </Text>
              </Pressable>
            );
          })}
        </VStack>
      </ScrollView>

      {atStart && <ScrollEdgeFade edge="top" color={colors.background.card} />}
      {atEnd && <ScrollEdgeFade edge="bottom" color={colors.background.card} />}
    </Box>
  );
}

function MobileNav({ activeSection, onSectionChange }: Readonly<SettingsNavProps>) {
  const { t } = useTranslation();
  const scrollRef = useRef<ScrollView>(null);
  const [scrollWidth, setScrollWidth] = useState(0);
  const pillLayoutsRef = useRef<Record<string, { x: number; width: number }>>({});

  useEffect(() => {
    const layout = pillLayoutsRef.current[activeSection];
    if (!layout || scrollWidth === 0 || !scrollRef.current) return;
    const targetX = Math.max(0, layout.x + layout.width / 2 - scrollWidth / 2);
    scrollRef.current.scrollTo({ x: targetX, animated: true });
  }, [activeSection, scrollWidth]);

  return (
    <ScrollView
      ref={scrollRef}
      horizontal
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={styles.mobileContainer}
      onLayout={(e) => setScrollWidth(e.nativeEvent.layout.width)}
    >
      {NAV_ITEMS.map((item) => {
        const active = item.key === activeSection;
        const danger = item.key === 'account';
        const IconComponent = item.icon;
        const iconColor = active
          ? danger
            ? colors.status.error.light
            : colors.primary[400]
          : colors.text.secondary;
        const textColor = iconColor;

        return (
          <Pressable
            key={item.key}
            testID={`settings_nav_${item.key}_tab`}
            onPress={() => onSectionChange(item.key)}
            onLayout={(e) => {
              pillLayoutsRef.current[item.key] = {
                x: e.nativeEvent.layout.x,
                width: e.nativeEvent.layout.width,
              };
            }}
            style={({ pressed }) => [
              styles.mobilePill,
              active
                ? danger
                  ? styles.mobilePillDanger
                  : styles.mobilePillActive
                : styles.mobilePillInactive,
              pressed && !active && styles.mobilePillPressed,
            ]}
            accessibilityRole="tab"
            accessibilityState={{ selected: active }}
          >
            <IconComponent size={ICON_SIZE - 2} color={iconColor} />
            <Text variant="body" weight="medium" color={textColor} style={styles.mobileLabel}>
              {t(item.labelKey)}
            </Text>
          </Pressable>
        );
      })}
    </ScrollView>
  );
}

export function SettingsNav({ activeSection, onSectionChange }: Readonly<SettingsNavProps>) {
  const { width } = useWindowDimensions();
  const isDesktop = width >= 768;

  if (isDesktop) {
    return <DesktopNav activeSection={activeSection} onSectionChange={onSectionChange} />;
  }

  return <MobileNav activeSection={activeSection} onSectionChange={onSectionChange} />;
}

const styles = StyleSheet.create({
  // flexShrink + minHeight let the card give way when the window is short so
  // its ScrollView takes over; without them it keeps its intrinsic height and
  // the overflow is clipped by an ancestor instead of scrolling. `overflow`
  // keeps the fades inside the rounded corners.
  desktopCard: {
    flexShrink: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  desktopItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: tokens.spacing.md,
    paddingVertical: tokens.spacing.sm,
    borderRadius: tokens.radius.md,
    borderWidth: 1,
    borderColor: colors.transparent,
    ...Platform.select({
      web: {
        cursor: 'pointer',
        userSelect: 'none',
        transitionProperty: 'background-color',
        transitionDuration: `${tokens.duration.fast}ms`,
      } as any,
    }),
  },
  desktopItemActive: {
    backgroundColor: colors.primary.muted,
    borderColor: colors.primary.borderActive,
  },
  desktopItemDanger: {
    backgroundColor: colors.status.error.bg,
    borderColor: colors.status.error.border,
  },
  desktopItemPressed: {
    backgroundColor: colors.interactive.hoverSubtle,
  },
  desktopLabel: {
    marginLeft: tokens.spacing.md,
  },
  mobileContainer: {
    flexDirection: 'row',
    gap: tokens.spacing.sm,
    paddingHorizontal: tokens.spacing.lg,
    paddingVertical: tokens.spacing.sm,
  },
  mobilePill: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: tokens.spacing.lg,
    paddingVertical: tokens.spacing.sm,
    borderRadius: tokens.radius.full,
    borderWidth: 1,
    borderColor: colors.transparent,
    ...Platform.select({
      web: {
        cursor: 'pointer',
        userSelect: 'none',
        transitionProperty: 'background-color',
        transitionDuration: `${tokens.duration.fast}ms`,
      } as any,
    }),
  },
  mobilePillActive: {
    backgroundColor: colors.primary.muted,
    borderColor: colors.primary.borderActive,
  },
  mobilePillDanger: {
    backgroundColor: colors.status.error.bg,
    borderColor: colors.status.error.border,
  },
  mobilePillInactive: {
    backgroundColor: colors.transparent,
  },
  mobilePillPressed: {
    backgroundColor: colors.interactive.hoverSubtle,
  },
  mobileLabel: {
    marginLeft: tokens.spacing.xs,
  },
});
